import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';

export type VideoStorageMode = 'pirouette_hosted' | 'byo_bucket';

interface VideoFeatureStatusRow {
  enabled: boolean;
  mode: VideoStorageMode;
}

interface VideoStorageSettingsRow {
  mode: VideoStorageMode;
  video_enabled: boolean;
  quota_gb: number;
  usage_bytes: number;
  byo_provider: ByoProvider | null;
  byo_endpoint: string | null;
  byo_region: string | null;
  byo_bucket: string | null;
  byo_prefix: string | null;
  byo_access_key_id: string | null;
  has_secret: boolean;
  byo_verified_at: string | null;
}

export type ByoProvider = 'cloudflare_r2' | 'backblaze_b2' | 'wasabi' | 'aws_s3';

export interface VideoStorageSettings {
  mode: VideoStorageMode;
  videoEnabled: boolean;
  quotaGb: number;
  usageBytes: number;
  byoProvider: ByoProvider | null;
  byoEndpoint: string | null;
  byoRegion: string | null;
  byoBucket: string | null;
  byoPrefix: string | null;
  byoAccessKeyId: string | null;
  hasSecret: boolean;
  byoVerifiedAt: string | null;
}

export interface SetByoBucketInput {
  provider: ByoProvider;
  endpoint: string;
  region: string;
  bucket: string;
  prefix: string;
  accessKeyId: string;
  secret: string;
}

interface RpcResult<T> {
  data: T | null;
  error: { message: string } | null;
}

interface VideoStorageRpc {
  (name: 'video_feature_status'): Promise<RpcResult<VideoFeatureStatusRow[]>>;
  (name: 'get_video_storage_settings'): Promise<RpcResult<VideoStorageSettingsRow[]>>;
  (name: 'set_video_storage_mode', args: { p_mode: VideoStorageMode }): Promise<RpcResult<null>>;
  (
    name: 'set_byo_bucket',
    args: {
      p_provider: ByoProvider;
      p_endpoint: string;
      p_region: string;
      p_bucket: string;
      p_prefix: string;
      p_access_key_id: string;
      p_secret: string;
    },
  ): Promise<RpcResult<null>>;
}

function videoStorageRpc(): VideoStorageRpc {
  return (async (name: string, args?: Record<string, unknown>) => {
    const result =
      args === undefined
        ? await supabase.rpc(name as never)
        : await supabase.rpc(name as never, args as never);
    return result as RpcResult<unknown>;
  }) as unknown as VideoStorageRpc;
}

function mapSettings(row: VideoStorageSettingsRow): VideoStorageSettings {
  return {
    mode: row.mode,
    videoEnabled: row.video_enabled,
    quotaGb: row.quota_gb,
    usageBytes: row.usage_bytes,
    byoProvider: row.byo_provider,
    byoEndpoint: row.byo_endpoint,
    byoRegion: row.byo_region,
    byoBucket: row.byo_bucket,
    byoPrefix: row.byo_prefix,
    byoAccessKeyId: row.byo_access_key_id,
    hasSecret: row.has_secret,
    byoVerifiedAt: row.byo_verified_at,
  };
}

export function useVideoEnabled() {
  return useQuery({
    queryKey: ['video-feature-status'],
    queryFn: async (): Promise<boolean> => {
      const { data, error } = await videoStorageRpc()('video_feature_status');
      if (error) throw new Error(error.message);
      return data?.[0]?.enabled === true;
    },
  });
}

export function useVideoStorageSettings() {
  return useQuery({
    queryKey: ['video-storage-settings'],
    queryFn: async (): Promise<VideoStorageSettings> => {
      const { data, error } = await videoStorageRpc()('get_video_storage_settings');
      if (error) throw new Error(error.message);
      const row = data?.[0];
      if (!row) throw new Error('video storage settings not found');
      return mapSettings(row);
    },
  });
}

export function useSetVideoStorageMode() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (mode: VideoStorageMode) => {
      const { error } = await videoStorageRpc()('set_video_storage_mode', { p_mode: mode });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['video-storage-settings'] });
      void qc.invalidateQueries({ queryKey: ['video-feature-status'] });
    },
  });
}

export function useSetByoBucket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: SetByoBucketInput) => {
      const { error } = await videoStorageRpc()('set_byo_bucket', {
        p_provider: input.provider,
        p_endpoint: input.endpoint,
        p_region: input.region,
        p_bucket: input.bucket,
        p_prefix: input.prefix,
        p_access_key_id: input.accessKeyId,
        p_secret: input.secret,
      });
      if (error) throw new Error(error.message);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['video-storage-settings'] });
    },
  });
}

export function useVerifyVideoBucket() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await supabase.functions.invoke<{ ok: true } | { error: string }>(
        'video-bucket-verify',
        { body: {} },
      );
      if (res.error) {
        const message =
          res.error instanceof Error ? res.error.message : 'video bucket verify failed';
        throw new Error(message);
      }
      if (res.data && 'error' in res.data) throw new Error(res.data.error);
    },
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['video-storage-settings'] });
    },
  });
}
